import type { InputFrame } from '../core/input';
import type { SimCommand, Sim } from '../sim/sim';
import type { StoredRunConfig } from './runPersistence';
import { storeConfig } from './runPersistence';

export const REPLAY_KEY = 'chronofall.replay.v1';

export interface ReplayBlock { count: number; frames: InputFrame[] }
export interface ReplayCommand { tick: number; command: SimCommand }
export interface ReplayData {
  version: 1; gameVersion: string; recordedAt: number; config: StoredRunConfig;
  blocks: ReplayBlock[]; commands: ReplayCommand[]; finalHash?: number;
}

const sameFrame = (a: InputFrame, b: InputFrame) => a.mx === b.mx && a.my === b.my && a.dash === b.dash && a.action === b.action;
const cloneFrames = (frames: readonly InputFrame[]) => frames.map((f) => ({ ...f }));

export class ReplayRecorder {
  readonly blocks: ReplayBlock[] = [];
  readonly commands: ReplayCommand[] = [];
  constructor(readonly gameVersion: string, readonly config: StoredRunConfig) {}

  frame(frames: readonly InputFrame[]): void {
    const last = this.blocks.at(-1);
    if (last && last.frames.length === frames.length && last.frames.every((f, i) => sameFrame(f, frames[i]))) last.count++;
    else this.blocks.push({ count: 1, frames: cloneFrames(frames) });
  }

  command(tick: number, command: SimCommand): void { this.commands.push({ tick, command: structuredClone(command) }); }
  data(finalHash?: number): ReplayData { return { version: 1, gameVersion: this.gameVersion, recordedAt: Date.now(), config: this.config, blocks: this.blocks, commands: this.commands, finalHash }; }
  static from(data: ReplayData): ReplayRecorder {
    const recorder = new ReplayRecorder(data.gameVersion, data.config);
    recorder.blocks.push(...data.blocks);
    recorder.commands.push(...data.commands);
    return recorder;
  }
}

export class ReplayPlayer {
  private block = 0;
  private offset = 0;
  constructor(readonly data: ReplayData) {}
  next(): InputFrame[] | null {
    const current = this.data.blocks[this.block];
    if (!current) return null;
    const frames = cloneFrames(current.frames);
    if (++this.offset >= current.count) { this.block++; this.offset = 0; }
    return frames;
  }
}

export function recorderFor(sim: Sim, gameVersion: string): ReplayRecorder { return new ReplayRecorder(gameVersion, storeConfig(sim.config)); }
export function saveReplay(data: ReplayData, storage: Storage = localStorage): boolean {
  try { storage.setItem(REPLAY_KEY, JSON.stringify(data)); return true; } catch (error) { console.warn('replay save failed', error); return false; }
}
export function loadReplay(gameVersion: string, storage: Storage = localStorage): ReplayData | null {
  try { const raw = storage.getItem(REPLAY_KEY); if (!raw) return null; const data = JSON.parse(raw) as ReplayData; return data.version === 1 && data.gameVersion === gameVersion ? data : null; } catch { return null; }
}
